import { Alert, Card, Empty, Table, Typography } from 'antd'
import dayjs from 'dayjs'
import ExportCsvButton from '../Datacenter/ExportCsvButton'
import { DashboardFilters, hasDashboardFilters } from './dashboardModel'
import { dashboardReportName, employeeUtilizationColumns } from './dashboardExport'
import { useEmployeeUtilization } from './dashboardQueries'
import { hasFiltersUtilizationIgnores, hasTrackedTime, sortByUnbillablePercent, utilizationFilters } from './employeeUtilizationModel'

export type Props = {
  start: string
  end: string
  filters: DashboardFilters
}

const EmployeeUtilizationSection = ({ start, end, filters }: Props) => {
  const appliedFilters = utilizationFilters(filters)
  // The picked end date as is: the endpoint itself includes the whole end day.
  const query = useEmployeeUtilization(start, end, appliedFilters)
  const employees = sortByUnbillablePercent(query.data?.employees ?? [])
  const hasDate = !!start && !!end
  const hasTime = hasTrackedTime(employees)

  return <Card
    title="Employee Utilization"
    style={{ marginTop: '25px' }}
    extra={<ExportCsvButton
      reportName={dashboardReportName('Employee Utilization', appliedFilters)}
      dateRange={{ start: dayjs(start), end: dayjs(end) }}
      columns={employeeUtilizationColumns}
      // Same rule as the empty state: when nobody tracked time there is nothing to export.
      rows={hasTime ? employees : []}
    />}
  >
    <Typography.Paragraph type="secondary">
      Hours tracked from {start} to {end}. Unbillable hours are time logged to unbillable categories, after break deductions;
      all time on jobs counts as billable. Submitted time only.
    </Typography.Paragraph>
    {hasDashboardFilters(appliedFilters) && <Alert type="info" showIcon style={{ marginBottom: 16 }} message="Showing everyone who logged time under the selected crew foremen." />}
    {hasFiltersUtilizationIgnores(filters) && <Alert type="info" showIcon style={{ marginBottom: 16 }} message="Only the crew foreman filter applies here: time is not tied to a division or sales person." />}
    {!hasDate
      ? <Empty description="Please select a time range at the top of the page." />
      : query.isError
      ? <Alert type="error" message="Error loading employee utilization" />
      : !query.isLoading && !hasTime
        ? <Empty description={`No time tracked from ${start} to ${end}`} />
        : <Table
            size="small"
            loading={query.isLoading}
            dataSource={employees}
            rowKey={record => record.crewMemberId}
            columns={employeeUtilizationColumns}
            pagination={{ showSizeChanger: true }}
          />}
  </Card>
}

export default EmployeeUtilizationSection
